<template>
    <section
        class="questions-container container"
        :class="[containerDesktopOnly ? 'container--only-desktop' : '']"
        :style="{...generateStylingVariables(content.style)}"
    >
        <zoe-organism-progress
            v-if="getValue('style.style.progress.showOnQuizStep') && !getValue(`${step}.quiz.style.progress.moveBeneathDisclaimer`)"
            :content="get('style.style.progress')"
            :step="step"
        />

        <div class="questions">
            <Transition
                v-for="(question, index) in $store.state.quiz.questionsData"
                :key="index"
                name="question-transition"
                @before-leave="beforeLeave"
                @enter="onEnter"
            >
                <div
                    v-if="index + 1 === $store.state.quiz.currentQuestion"
                    :key="index"
                    :class="`question question-${index + 1}`"
                    :data-question-type="question._info.type"
                >
                    <div class="question__inner">
                        <div class="question__wrapper">
                            <zoe-atom-text
                                v-if="stringIsNotEmpty(question.text.value)"
                                class="question__header"
                                :item="question.text.value"
                            />

                            <div class="question__body">
                                <component
                                    :is="questionType(question)"
                                    :index="index"
                                    :content="question"
                                    :pre-hook="beforeGoToNextQuestion"
                                    :after-hook="beforeGoToPreviousQuestion"
                                />
                            </div>
                        </div>

                        <template v-if="question.disclaimer">
                            <template v-if="question.disclaimer.text">
                                <div
                                    v-if="stringIsNotEmpty(question.disclaimer.text.value)"
                                    class="question__disclaimer"
                                >
                                    <zoe-atom-media
                                        class="question__icon"
                                        :item="question.disclaimer.icon.value"
                                    />

                                    <zoe-atom-text
                                        class="question__text"
                                        :item="question.disclaimer.text.value"
                                    />
                                </div>

                                <zoe-organism-progress
                                    v-if="getValue('style.style.progress.showOnQuizStep') && getValue(`${step}.quiz.style.progress.moveBeneathDisclaimer`)"
                                    :content="get('style.style.progress')"
                                    :step="step"
                                />
                            </template>
                            <template v-else>
                                <div
                                    v-if="stringIsNotEmpty(question.disclaimer.value)"
                                    class="question__disclaimer"
                                >
                                    <zoe-atom-text
                                        class="question__text"
                                        :item="question.disclaimer.value"
                                    />
                                </div>

                                <zoe-organism-progress
                                    v-if="getValue('style.style.progress.showOnQuizStep') && getValue(`${step}.options.progress.moveBeneathDisclaimer`)"
                                    :content="get('style.style.progress')"
                                    :step="step"
                                />
                            </template>
                        </template>
                        <template v-else>
                            <div
                                v-if="stringIsNotEmpty(question['disclaimer-text'].value)"
                                class="question__disclaimer"
                            >
                                <zoe-atom-media
                                    class="question__icon"
                                    :item="question['disclaimer-icon'].value"
                                />

                                <zoe-atom-text
                                    class="question__text"
                                    :item="question['disclaimer-text'].value"
                                />
                            </div>

                            <zoe-organism-progress
                                v-if="getValue('style.style.progress.showOnQuizStep') && getValue(`${step}.quiz.style.progress.moveBeneathDisclaimer`)"
                                :content="get('style.style.progress')"
                                :step="step"
                            />
                        </template>
                    </div>
                </div>
            </Transition>
        </div>
    </section>
</template>

<script>
import ZoeAtomText from '@atoms/text/v1.0.0/zoe-atom-text.vue';
import ZoeAtomMedia from '@atoms/media/v1.0.0/zoe-atom-media.vue';
// json5-ignore
import ZoeOrganismProgress from '@organisms/progress/v1.0.0/zoe-organism-progress.vue';
import ZoeQuizAddressAutocomplete from '@questions/types/address-autocomplete/v1.0.0/zoe-quiz-address-autocomplete.vue';
import ZoeQuizButtons from '@questions/types/buttons/v2.0.0/zoe-quiz-buttons.vue';
import ZoeQuizCheckbox from '@questions/types/checkbox/v2.0.0/zoe-quiz-checkbox.vue';
import ZoeQuizRadioButton from '@questions/types/radio-button/v2.0.0/zoe-quiz-radio-button.vue';
import ZoeQuizRangeSlider from '@questions/types/range-slider/v2.0.0/zoe-quiz-range-slider.vue';
import ZoeQuizSelect from '@questions/types/select/v2.0.0/zoe-quiz-select.vue';
import ZoeQuizTextfield from '@questions/types/textfield/v2.0.0/zoe-quiz-textfield.vue';
import ZoeQuizTogglePicker from '@questions/types/toggle-picker/v2.0.0/zoe-quiz-toggle-picker.vue';
import ZoeQuizZipcode from '@questions/types/zipcode/v2.0.0/zoe-quiz-zipcode.vue';
import ZoeQuizButtonsWithImage from '@questions/types/buttons-with-image/v2.0.0/zoe-quiz-buttons-with-image.vue';
import General from '../../../../../../../resources/frontend/js/mixins/general.vue';
import Page from '../../../../../../../resources/frontend/js/mixins/page.vue';
import Component from '../../../../../../resources/mixins/component/component.vue';
import { generateStylingVariables } from '../../../../../../resources/mixins/styling/styling';

export default {
    components: {
        ZoeAtomText,
        ZoeAtomMedia,
        ZoeOrganismProgress,
        ZoeQuizAddressAutocomplete,
        ZoeQuizButtons,
        ZoeQuizButtonsWithImage,
        ZoeQuizCheckbox,
        ZoeQuizRadioButton,
        ZoeQuizRangeSlider,
        ZoeQuizSelect,
        ZoeQuizTextfield,
        ZoeQuizTogglePicker,
        ZoeQuizZipcode,
    },

    mixins: [General, Page, Component],

    props: {
        step: {
            type: String,
            default: '',
        },

        containerDesktopOnly: {
            type: Boolean,
            default: false,
            required: false,
        },
    },

    methods: {
        generateStylingVariables,
        questionType(question) {
            return `zoe-quiz-${question._info.type}`;
        },

        beforeGoToNextQuestion() {
            const showOverlay = this.getValue(`${this.step}.overlay.enable`);

            if (showOverlay) {
                this.$emit('toggleOverlay');
            }
        },

        beforeGoToPreviousQuestion() {},

        beforeLeave(el) {
            const parent = el.parentElement;
            parent.style.height = el.clientHeight + 'px';
        },

        onEnter(el) {
            const parent = el.parentElement;

            setTimeout(() => {
                parent.style.height = el.clientHeight + 'px';
            }, 50);

            setTimeout(() => {
                parent.style.height = '';
            }, 450);
        },
    },
};
</script>

<style lang="scss">
@use 'step-questions';

@layer step {
    .questions-container {
        --background: v-bind('content.style.background.value');
    }
}

.question-transition-leave-active {
    transition: all .4s ease;
}

.question-transition-enter-active {
    transition: all .4s ease .4s;
}

.question-transition-leave-to,
.question-transition-enter-from {
    position: absolute !important;
    top: 0;
    opacity: 0;
    width: 100%;
}
</style>
